import { Directive, ElementRef, HostListener, Input, OnInit, OnDestroy } from '@angular/core';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';

@Directive({
  selector: '[appSwipeToClose]',
})
export class SwipeToCloseDirective implements OnInit, OnDestroy {
  @Input() appSwipeToClose: MatBottomSheetRef<any>;
  @Input() swipeThreshold: number = 100; // Umbral en píxeles para cerrar
  @Input() swipeZoneHeight: number = 80; // Altura de la zona donde se activa el swipe (en píxeles)
  @Input() forceSwipeInZone: boolean = true; // Forzar swipe en la zona superior sin validar scroll

  private startY: number = 0;
  private currentY: number = 0;
  private isDragging: boolean = false;
  private element: HTMLElement;
  private initialTouchY: number = 0;
  private swipeZoneDetected: boolean = false;
  private touchStartTime: number = 0; // Para detectar taps vs swipes

  constructor(private el: ElementRef) {
    this.element = this.el.nativeElement;
  }

  ngOnInit(): void {
    // Configuración mejorada para compatibilidad con Android
    this.element.style.touchAction = 'pan-y'; // Permitir solo scroll vertical
  }

  ngOnDestroy(): void {
    // Limpiar estilos al destruir
    if (this.element) {
      this.element.style.touchAction = '';
    }
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    // Resetear estado previo
    this.isDragging = false;
    this.swipeZoneDetected = false;
    this.touchStartTime = Date.now();

    // Validar que tenemos al menos un touch
    if (!event.touches || event.touches.length === 0) {
      return;
    }

    // Guardar la posición Y inicial del touch
    this.initialTouchY = event.touches[0].clientY;
    this.startY = event.touches[0].clientY;

    // Obtener la posición del elemento de manera más robusta
    const rect = this.element.getBoundingClientRect();
    const touchY = event.touches[0].clientY;
    const relativeY = touchY - rect.top;

    // Debug para Android
    console.log('TouchStart - Android Debug:', {
      touchY,
      rectTop: rect.top,
      relativeY,
      swipeZoneHeight: this.swipeZoneHeight,
    });

    // Solo activar el swipe si el toque está en la zona superior (primeros N píxeles)
    if (relativeY > this.swipeZoneHeight) {
      console.log('TouchStart - Fuera de zona de swipe');
      return;
    }

    this.swipeZoneDetected = true;
    console.log('TouchStart - En zona de swipe, iniciando dragging');

    // Si forceSwipeInZone está habilitado, permitir siempre el gesto en la zona superior
    if (this.forceSwipeInZone) {
      this.isDragging = true;

      // Prevenir el comportamiento de pull-to-refresh del navegador
      // En Android es crucial hacer esto inmediatamente
      if (event.cancelable) {
        event.preventDefault();
      }
      event.stopPropagation();

      // Encontrar el contenedor del bottom sheet y ajustar su posición
      const bottomSheetContainer = this.findBottomSheetContainer();
      if (bottomSheetContainer) {
        bottomSheetContainer.style.transition = 'none';
        console.log('TouchStart - Container encontrado y preparado');
      }
      return;
    }

    // Lógica original para otros casos (simplificada para Android)
    this.isDragging = true;

    // Prevenir comportamientos del navegador
    if (event.cancelable) {
      event.preventDefault();
    }
    event.stopPropagation();

    // Preparar contenedor
    const bottomSheetContainer = this.findBottomSheetContainer();
    if (bottomSheetContainer) {
      bottomSheetContainer.style.transition = 'none';
    }
  }

  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    // Si no estamos arrastrando, salir
    if (!this.isDragging) {
      return;
    }

    // Validar que tenemos al menos un touch
    if (!event.touches || event.touches.length === 0) {
      console.log('TouchMove - No hay touches disponibles');
      return;
    }

    this.currentY = event.touches[0].clientY;
    const deltaY = this.currentY - this.startY;

    // Debug para Android
    console.log('TouchMove - Android Debug:', {
      currentY: this.currentY,
      startY: this.startY,
      deltaY: deltaY,
      isDragging: this.isDragging,
    });

    // Solo permitir arrastrar hacia abajo
    if (deltaY > 0) {
      // Prevenir SIEMPRE el comportamiento de pull-to-refresh cuando estamos arrastrando
      // Esto es crítico para Android
      if (event.cancelable) {
        event.preventDefault();
      }
      event.stopPropagation();

      // Mover solo el contenedor del bottom sheet (no el elemento individual)
      const bottomSheetContainer = this.findBottomSheetContainer();
      if (bottomSheetContainer) {
        const translateY = Math.min(deltaY, window.innerHeight * 0.8); // Limitar el movimiento
        bottomSheetContainer.style.transform = `translateY(${translateY}px)`;

        // Reducir opacidad gradualmente del contenedor
        const opacity = Math.max(0.3, 1 - translateY / 300);
        bottomSheetContainer.style.opacity = opacity.toString();

        console.log('TouchMove - Container movido:', { translateY, opacity });
      }
    } else if (deltaY < -10) {
      // Si intenta arrastrar hacia arriba más de 10px, cancelar el gesto
      console.log('TouchMove - Movimiento hacia arriba detectado, cancelando');
      this.isDragging = false;

      const bottomSheetContainer = this.findBottomSheetContainer();
      if (bottomSheetContainer) {
        bottomSheetContainer.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';
        bottomSheetContainer.style.transform = 'translateY(0)';
        bottomSheetContainer.style.opacity = '1';
      }
    }
  }
  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    console.log('TouchEnd - Android Debug:', {
      isDragging: this.isDragging,
      currentY: this.currentY,
      startY: this.startY,
    });

    if (!this.isDragging) {
      return;
    }

    const deltaY = this.currentY - this.startY;
    const touchDuration = Date.now() - this.touchStartTime;
    this.isDragging = false;

    console.log('TouchEnd - Calculando:', {
      deltaY,
      touchDuration,
      swipeThreshold: this.swipeThreshold,
    });

    const bottomSheetContainer = this.findBottomSheetContainer();
    if (bottomSheetContainer) {
      bottomSheetContainer.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';
    }

    // Determinar si debe cerrarse basado en distancia Y velocidad
    const shouldClose = deltaY > this.swipeThreshold || (deltaY > 50 && touchDuration < 300); // Swipe rápido

    if (shouldClose) {
      console.log('TouchEnd - Cerrando bottom sheet');
      this.closeBottomSheet();
    } else {
      console.log('TouchEnd - Restaurando posición');
      // Restaurar posición original
      if (bottomSheetContainer) {
        bottomSheetContainer.style.transform = 'translateY(0)';
        bottomSheetContainer.style.opacity = '1';
      }
    }
  }

  @HostListener('touchcancel', ['$event'])
  onTouchCancel(event: TouchEvent): void {
    console.log('TouchCancel - Android Debug:', {
      isDragging: this.isDragging,
    });

    // Restaurar estado si el gesto fue cancelado
    if (this.isDragging) {
      this.isDragging = false;

      const bottomSheetContainer = this.findBottomSheetContainer();
      if (bottomSheetContainer) {
        bottomSheetContainer.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';
        bottomSheetContainer.style.transform = 'translateY(0)';
        bottomSheetContainer.style.opacity = '1';
      }

      console.log('TouchCancel - Estado restaurado');
    }
  }

  private closeBottomSheet(): void {
    console.log('closeBottomSheet - Iniciando cierre');

    if (this.appSwipeToClose) {
      const bottomSheetContainer = this.findBottomSheetContainer();

      // Animar hacia abajo antes de cerrar
      if (bottomSheetContainer) {
        bottomSheetContainer.style.transform = 'translateY(100%)';
        bottomSheetContainer.style.opacity = '0';
        console.log('closeBottomSheet - Animación aplicada');
      }

      // Cerrar después de la animación
      setTimeout(() => {
        console.log('closeBottomSheet - Ejecutando dismiss');
        this.appSwipeToClose.dismiss();
      }, 300);
    } else {
      console.log('closeBottomSheet - No hay referencia al bottom sheet');
    }
  }

  private isInSwipeZone(element: HTMLElement): boolean {
    // Verificar si el elemento está en la zona de swipe (divisor blanco o contenedor)
    let current = element;
    while (current && current !== document.body) {
      // Buscar clases que indiquen que estamos en la zona de swipe
      if (current.classList.contains('white-divider-container') || current.classList.contains('white-divider') || current.classList.contains('divider-zone')) {
        return true;
      }
      current = current.parentElement;
    }
    return false;
  }

  private findScrollableParent(element: HTMLElement): HTMLElement | null {
    let parent = element;
    while (parent && parent !== document.body) {
      const overflowY = window.getComputedStyle(parent).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  private findBottomSheetContainer(): HTMLElement | null {
    // Buscar el contenedor padre con la clase mat-bottom-sheet-container
    let parent = this.element.parentElement;

    while (parent && parent !== document.body) {
      if (parent.classList.contains('mat-bottom-sheet-container')) {
        return parent;
      }
      parent = parent.parentElement;
    }

    return null;
  }
}
